/**
 * What each taunt sound *is*, as data.
 *
 * Same split as `hud/tag.ts`: the decision here, the browser API next door in
 * `taunt-audio.ts`. A patch table is worth testing without an `AudioContext`,
 * and the one property that actually matters — that every wire value the
 * server can send maps to something audible — is a property of this table
 * rather than of the code that plays it.
 *
 * There is no audio file anywhere in this repo and this is why. A taunt is a
 * short two-tone chirp from one oscillator through one gain envelope, so the
 * whole mechanic ships as twenty numbers instead of four assets, an asset
 * pipeline, and a loading state to get wrong before the first round.
 */

export interface TauntPatch {
  wave: OscillatorType;
  /** Pitch swept from this to `endHz` over the note. */
  startHz: number;
  endHz: number;
  durationMs: number;
  /**
   * Peak gain before distance attenuation.
   *
   * Trimmed per patch rather than shared, because a sawtooth at the same
   * amplitude as a sine is roughly twice as loud to a listener and the four
   * taunts have to be equally informative — a hunter should not learn more
   * from one hider's noise than another's.
   */
  gain: number;
}

/**
 * Indexed by `TauntSoundId`, so order is the wire format and this array is
 * append-only in exactly the way the enum it mirrors is.
 */
export const TAUNT_PATCHES: readonly TauntPatch[] = [
  { wave: "sine", startHz: 470, endHz: 700, durationMs: 260, gain: 0.5 },
  { wave: "triangle", startHz: 900, endHz: 560, durationMs: 220, gain: 0.42 },
  { wave: "sawtooth", startHz: 210, endHz: 175, durationMs: 340, gain: 0.22 },
  { wave: "square", startHz: 640, endHz: 980, durationMs: 170, gain: 0.18 },
];

/**
 * The patch for a sound id, falling back to the first rather than to silence.
 *
 * The id arrives over the network, so it can be anything a byte can hold. A
 * client one deploy behind a server that added a fifth taunt should play the
 * wrong noise, not no noise: the position is the information, the timbre is
 * flavour, and going silent would quietly cost this client the whole mechanic
 * while looking like nothing at all.
 */
export function tauntPatch(sound: number): TauntPatch {
  const i = Number.isInteger(sound) && sound >= 0 && sound < TAUNT_PATCHES.length ? sound : 0;
  return TAUNT_PATCHES[i]!;
}
